import { Input, Popover } from '@any-ui-react/core';
import { DatePicker as MantineDatePicker } from '@mantine/dates';
import { format, isValid, isWeekend } from 'date-fns';
import isString from 'lodash-es/isString';
import { forwardRef, type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DATETIME_FORMATS, DateUtils } from '@/common/utils';
import { MaterialIcons } from '../material-icons';

interface Props {
  className?: string;
  clearable?: boolean;
  defaultDate?: string;
  disabled?: boolean;
  error?: string;
  excludeWeekends?: boolean;
  label?: string;
  maxDate?: Date;
  minDate?: Date;
  placeholder?: string;
  required?: boolean;
  value?: string;
  withinPortal?: boolean;
  onChange: (value?: string) => void;
}

export const DatePicker = forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      clearable = true,
      defaultDate,
      disabled,
      error,
      excludeWeekends,
      label,
      maxDate,
      minDate,
      placeholder,
      required,
      value,
      withinPortal = true,
      onChange,
    },
    ref,
  ) => {
    const [openDatePicker, setOpenDatePicker] = useState(false);
    const [dateInput, setDateInput] = useState(value);
    const [previousDateInput, setPreviousDateInput] = useState('');
    const refDatePicker = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (isValid(value)) {
        setDateInput(value);
      }
    }, [value]);

    const isValidStrict = useMemo(
      () => isString(dateInput) && /\d{4}-\d{2}-\d{2}/.test(dateInput) && isValid(DateUtils.parseDate(dateInput)),
      [dateInput],
    );

    const excludeDate = useCallback(
      (date: Date) => {
        if (!excludeWeekends) return false;
        return isWeekend(date);
      },
      [excludeWeekends],
    );

    return (
      <div ref={ref} className={className}>
        <Popover
          closeOnClickOutside={true}
          offset={0}
          opened={openDatePicker}
          position="bottom-start"
          returnFocus={true}
          withinPortal={withinPortal}
          onChange={setOpenDatePicker}
        >
          <Popover.Target>
            <Input.Wrapper
              onBlur={event => {
                if (!isValidStrict && dateInput) {
                  setDateInput(previousDateInput || undefined);
                  onChange(previousDateInput || undefined);
                }

                if (!refDatePicker.current?.contains(event.relatedTarget)) {
                  setOpenDatePicker(false);
                }
              }}
              onClick={() => {
                if (disabled) {
                  return;
                }

                setOpenDatePicker(prev => !prev);
              }}
              onFocus={() => setPreviousDateInput(value || '')}
            >
              {label && <Input.Label required={required}>{label}</Input.Label>}
              <div className="relative">
                <Input
                  disabled={disabled}
                  error={error}
                  placeholder={placeholder}
                  value={dateInput || ''}
                  onChange={e => {
                    const newValue = e.target.value;
                    setDateInput(newValue);
                    onChange(!newValue ? undefined : newValue);
                  }}
                />

                {clearable && dateInput && !disabled ? (
                  <MaterialIcons
                    className="absolute right-2 top-2 cursor-pointer text-gray-6"
                    fill={true}
                    fontSize="16px"
                    iconName="cancel"
                    onClick={(event: MouseEvent) => {
                      event.stopPropagation();
                      setDateInput(undefined);
                      onChange(undefined);
                    }}
                  />
                ) : (
                  <MaterialIcons iconName="keyboard_arrow_down" className="absolute right-2 top-2 cursor-pointer" />
                )}
              </div>
              {error && <Input.Error className="mt-1">{error}</Input.Error>}
            </Input.Wrapper>
          </Popover.Target>

          {!disabled && (
            <Popover.Dropdown
              ref={refDatePicker}
              className="absolute max-h-[26rem] rounded border border-gray-2 bg-white shadow-sm"
            >
              <MantineDatePicker
                allowDeselect={clearable}
                className="bg-white"
                defaultDate={DateUtils.coalesce(defaultDate, dateInput)}
                excludeDate={excludeDate}
                highlightToday={true}
                maxDate={maxDate}
                minDate={minDate}
                value={DateUtils.coalesce(dateInput)}
                onChange={v => {
                  if (v) {
                    setDateInput(format(v, DATETIME_FORMATS.yyyyMMdd));
                    onChange(format(v, DATETIME_FORMATS.yyyyMMdd));
                    setOpenDatePicker(false);
                  }
                }}
              />
            </Popover.Dropdown>
          )}
        </Popover>
      </div>
    );
  },
);
